import { useEffect } from "react";
import { Phone, PhoneOff } from "@fg/icons";

export type OutgoingPhase = "ringing" | "declined" | "no-answer";

/**
 * The caller's side of a call that nobody has picked up yet — Teams' "Calling…"
 * screen. Before this the caller sat alone in an empty room with a "You're the
 * only one here" pop-up, no ringback and no way to cancel the ring; the callee
 * kept ringing for 45 s after the caller had given up.
 */
export function OutgoingCallScreen({
    phase,
    calleeLabel,
    audioOnly,
    onCancel,
    onRetry,
    onClose
}: {
    phase: OutgoingPhase;
    calleeLabel: string;
    audioOnly: boolean;
    onCancel: () => void;
    onRetry: () => void;
    onClose: () => void;
}) {
    useEffect(() => {
        if (phase !== "ringing") return;
        return startRingback();
    }, [phase]);

    return (
        <div
            role="status"
            aria-live="polite"
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-6 bg-zinc-950/95 p-6 text-white"
        >
            <div className="relative">
                {phase === "ringing" ? (
                    <>
                        <div className="absolute inset-0 animate-ping rounded-full bg-primary/25" />
                        <div className="absolute inset-0 animate-ping rounded-full bg-primary/15" style={{ animationDelay: "0.6s" }} />
                    </>
                ) : null}
                <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-primary/25 text-3xl font-semibold">
                    {initials(calleeLabel)}
                </div>
            </div>
            <div className="text-center">
                <div className="max-w-xs truncate text-xl font-semibold">{calleeLabel}</div>
                <div className="mt-1 text-sm text-white/60">
                    {phase === "ringing"
                        ? audioOnly ? "Calling…" : "Video calling…"
                        : phase === "declined" ? "Declined your call" : "No answer"}
                </div>
            </div>
            {phase === "ringing" ? (
                <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Cancel call"
                    title="Cancel call"
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 hover:bg-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                    <PhoneOff className="h-6 w-6" />
                </button>
            ) : (
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onRetry}
                        className="flex h-11 items-center gap-2 rounded-full bg-emerald-600 px-5 text-sm font-semibold hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        <Phone className="h-4 w-4" /> Call again
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-11 rounded-full bg-white/10 px-5 text-sm hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        Close
                    </button>
                </div>
            )}
        </div>
    );
}

function initials(name: string): string {
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/**
 * Ringback — the "tuut … tuut" the caller hears while the other side rings
 * (440 + 480 Hz, 2 s on, 4 s off). Quiet, and stops when the returned
 * function is called.
 */
function startRingback(): () => void {
    const Ctx = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return () => undefined;
    let ctx: AudioContext | null = null;
    let timer: number | undefined;
    let stopped = false;
    try {
        ctx = new Ctx();
        const burst = () => {
            if (!ctx || stopped) return;
            const gain = ctx.createGain();
            gain.connect(ctx.destination);
            const t0 = ctx.currentTime;
            gain.gain.setValueAtTime(0.0001, t0);
            gain.gain.exponentialRampToValueAtTime(0.05, t0 + 0.05);
            gain.gain.setValueAtTime(0.05, t0 + 1.9);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 2);
            for (const freq of [440, 480]) {
                const osc = ctx.createOscillator();
                osc.frequency.value = freq;
                osc.connect(gain);
                osc.start(t0);
                osc.stop(t0 + 2);
            }
        };
        burst();
        timer = window.setInterval(burst, 6000);
    } catch {
        /* autoplay blocked — the screen still shows the call is ringing */
    }
    return () => {
        stopped = true;
        if (timer !== undefined) window.clearInterval(timer);
        void ctx?.close().catch(() => undefined);
    };
}
